/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { matchPath, useLocation, useParams } from 'react-router-dom'

import type { IconType } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { RoutableBreadcrumb, VersionNumber } from '@modules/ui-types'

import DeploymentTrustRelationshipHeader from '@/components/DeploymentTrustRelationships/DeploymentTrustRelationshipHeader'
import CreateDeploymentTrustRelationshipHeader from '@/components/DeploymentTrustRelationships/CreateDeploymentTrustRelationship/CreateDeploymentTrustRelationshipHeader'
import {
  deploymentActivityCrumbs,
  deploymentActivitySliderCrumbs,
  deploymentAddTrustCrumbs,
  deploymentApiConsoleCrumbs,
  deploymentOneConsoleCrumbs,
  deploymentEditAdvancedCrumbs,
  deploymentEditCrumbs,
  deploymentEditTrustCrumbs,
  deploymentGettingStartedCrumbs,
  deploymentHealthCrumbs,
  deploymentHealthIssueDetailsCrumbs,
  deploymentHeapDumpsCrumbs,
  deploymentIlmMigrationCrumbs,
  deploymentLogsCrumbs,
  deploymentMetricsCrumbs,
  deploymentOperationsCrumbs,
  deploymentOverviewCrumbs,
  deploymentSecurityCrumbs,
  deploymentSliderCrumbs,
  deploymentSnapshotCrumbs,
  deploymentSnapshotsCrumbs,
} from '@/lib/crumbBuilder'
import { getSupportedSliderInstanceTypes } from '@/lib/sliders/support'
import { getSliderPrettyName } from '@/lib/sliders/messages'
import { getSliderIconType } from '@/lib/sliders/icons'
import {
  clusterApiConsoleUrl,
  clusterOneConsoleUrl,
  clusterSnapshotsUrl,
  clusterSnapshotUrl,
  createDeploymentTrustRelationshipUrl,
  deploymentActivityUrl,
  deploymentAdvancedEditUrl,
  deploymentEditUrl,
  deploymentGettingStartedUrl,
  deploymentHealthProblemDetailsUrl,
  deploymentHealthStatusUrl,
  deploymentIlmMigrationUrl,
  heapDumpsUrl,
  logsMonitoringUrl,
  manageDeploymentTrustRelationshipUrl,
  operationsUrl,
  performanceMetricsUrl,
  securityUrl,
  sliderActivityUrl,
  sliderUrl,
} from '@/lib/urlBuilder'

import { HealthStatusDetailsHeader } from '../HealthStatusDetails/HealthStatusDetailsHeader'

import type { RouteParams, AllProps as DeploymentHeaderProps } from './types'
import type { ReactNode } from 'react'

type HeaderDefinition = {
  breadcrumbs: RoutableBreadcrumb[]
  title?: ReactNode
  iconType?: IconType
  shouldHideRegion?: boolean
  hideHorizontalRule?: boolean
  component?: React.ComponentType<DeploymentHeaderProps> // custom implementation which overrides title/icon/region
}

export function useHeaderDefinition({
  deploymentDisplayName,
  version,
}: {
  deploymentDisplayName?: string
  version?: VersionNumber | null
}): HeaderDefinition {
  const { pathname } = useLocation()
  const { deploymentId, snapshotName, issue, trustRelationshipId } = useParams<RouteParams>()

  if (!deploymentId) {
    // sanity; we should only be calling this hook from a place where we have a
    // deploymentId in the route path, but handle gracefully just in case
    return {
      breadcrumbs: [],
    }
  }

  if (matchPath(deploymentEditUrl(deploymentId), pathname)) {
    return {
      title: <FormattedMessage id='deployment-header.edit' defaultMessage='Edit' />,
      breadcrumbs: deploymentEditCrumbs({ deploymentId, deploymentDisplayName }),
    }
  }

  if (matchPath(deploymentHealthStatusUrl(deploymentId), pathname)) {
    return {
      title: <FormattedMessage id='deployment-header.monitoring' defaultMessage='Monitoring' />,
      breadcrumbs: deploymentHealthCrumbs({ deploymentId, deploymentDisplayName }),
    }
  }

  if (issue && matchPath(deploymentHealthProblemDetailsUrl(deploymentId, issue), pathname)) {
    return {
      component: HealthStatusDetailsHeader,
      breadcrumbs: deploymentHealthIssueDetailsCrumbs({
        deploymentId,
        deploymentDisplayName,
        issue,
      }),
    }
  }

  if (matchPath(deploymentAdvancedEditUrl(deploymentId), pathname)) {
    return {
      title: (
        <FormattedMessage id='deployment-header.advanced-edit' defaultMessage='Advanced edit' />
      ),
      breadcrumbs: deploymentEditAdvancedCrumbs({ deploymentId }),
    }
  }

  if (matchPath(performanceMetricsUrl(deploymentId), pathname)) {
    return {
      title: <FormattedMessage id='deployment-header.performance' defaultMessage='Performance' />,
      breadcrumbs: deploymentMetricsCrumbs({ deploymentId, deploymentDisplayName }),
    }
  }

  if (matchPath(logsMonitoringUrl(deploymentId), pathname)) {
    return {
      title: (
        <FormattedMessage
          id='deployment-header.logging-and-monitoring'
          defaultMessage='Logs and metrics'
        />
      ),
      breadcrumbs: deploymentLogsCrumbs({ deploymentId, deploymentDisplayName }),
    }
  }

  if (matchPath(deploymentActivityUrl(deploymentId), pathname)) {
    return {
      title: <FormattedMessage id='deployment-header.activity' defaultMessage='Activity' />,
      breadcrumbs: deploymentActivityCrumbs({ deploymentId, deploymentDisplayName }),
    }
  }

  if (matchPath(deploymentGettingStartedUrl(deploymentId), pathname)) {
    return {
      title: <FormattedMessage id='deployment-header.create' defaultMessage='Create' />,
      breadcrumbs: deploymentGettingStartedCrumbs({ deploymentId, deploymentDisplayName }),
    }
  }

  if (pathname === clusterOneConsoleUrl(deploymentId)) {
    return {
      title: (
        <FormattedMessage
          id='deployment-header.elasticsearch-one-console'
          defaultMessage='Console'
        />
      ),
      iconType: getSliderIconType({ sliderInstanceType: `elasticsearch` }),
      breadcrumbs: deploymentOneConsoleCrumbs({ deploymentId, deploymentDisplayName }),
    }
  }

  for (const sliderInstanceType of getSupportedSliderInstanceTypes()) {
    if (matchPath(sliderUrl(deploymentId, sliderInstanceType), pathname)) {
      return {
        title: <FormattedMessage {...getSliderPrettyName({ sliderInstanceType, version })} />,
        iconType: getSliderIconType({ sliderInstanceType }),
        breadcrumbs: deploymentSliderCrumbs({
          deploymentId,
          sliderInstanceType,
          deploymentDisplayName,
          version,
        }),
      }
    }

    if (matchPath(sliderActivityUrl(deploymentId, sliderInstanceType), pathname)) {
      return {
        title: (
          <FormattedMessage
            id='deployment-header.slider-activity'
            defaultMessage='{sliderName} activity'
            values={{
              sliderName: (
                <FormattedMessage {...getSliderPrettyName({ sliderInstanceType, version })} />
              ),
            }}
          />
        ),
        iconType: getSliderIconType({ sliderInstanceType }),
        breadcrumbs: deploymentActivitySliderCrumbs({
          deploymentId,
          sliderInstanceType,
          deploymentDisplayName,
        }),
      }
    }
  }

  if (matchPath(clusterSnapshotsUrl(deploymentId), pathname)) {
    return {
      title: (
        <FormattedMessage
          id='deployment-header.elasticsearch-snapshots'
          defaultMessage='Snapshots'
        />
      ),
      iconType: getSliderIconType({ sliderInstanceType: `elasticsearch` }),
      breadcrumbs: deploymentSnapshotsCrumbs({ deploymentId, deploymentDisplayName }),
    }
  }

  if (snapshotName && matchPath(clusterSnapshotUrl(deploymentId, snapshotName), pathname)) {
    return {
      title: (
        <FormattedMessage id='deployment-header.elasticsearch-snapshot' defaultMessage='Snapshot' />
      ),
      iconType: getSliderIconType({ sliderInstanceType: `elasticsearch` }),
      breadcrumbs: deploymentSnapshotCrumbs({
        deploymentId,
        snapshotName,
        deploymentDisplayName,
      }),
    }
  }

  if (matchPath(clusterApiConsoleUrl(deploymentId), pathname)) {
    return {
      title: (
        <FormattedMessage
          id='deployment-header.elasticsearch-console'
          defaultMessage='API console'
        />
      ),
      iconType: getSliderIconType({ sliderInstanceType: `elasticsearch` }),
      breadcrumbs: deploymentApiConsoleCrumbs({ deploymentId, deploymentDisplayName }),
    }
  }

  if (matchPath(securityUrl(deploymentId), pathname)) {
    return {
      title: <FormattedMessage id='deployment-header.security' defaultMessage='Security' />,
      breadcrumbs: deploymentSecurityCrumbs({ deploymentId, deploymentDisplayName }),
    }
  }

  if (matchPath(createDeploymentTrustRelationshipUrl(deploymentId, undefined), pathname)) {
    return {
      breadcrumbs: deploymentAddTrustCrumbs({ deploymentId, deploymentDisplayName }),
      component: CreateDeploymentTrustRelationshipHeader,
    }
  }

  if (
    matchPath(createDeploymentTrustRelationshipUrl(deploymentId, 'accounts'), pathname) ||
    matchPath(createDeploymentTrustRelationshipUrl(deploymentId, 'external'), pathname) ||
    matchPath(createDeploymentTrustRelationshipUrl(deploymentId, 'direct'), pathname)
  ) {
    return {
      breadcrumbs: deploymentAddTrustCrumbs({ deploymentId, deploymentDisplayName }),
      component: DeploymentTrustRelationshipHeader,
    }
  }

  const editTrustHeaderDefinition: HeaderDefinition = {
    breadcrumbs: deploymentEditTrustCrumbs({ deploymentId, deploymentDisplayName }),
    component: DeploymentTrustRelationshipHeader,
  }

  if (
    trustRelationshipId &&
    matchPath(
      manageDeploymentTrustRelationshipUrl(
        deploymentId,
        undefined,
        'accounts',
        trustRelationshipId,
      ),
      pathname,
    )
  ) {
    return editTrustHeaderDefinition
  }

  if (
    trustRelationshipId &&
    matchPath(
      manageDeploymentTrustRelationshipUrl(
        deploymentId,
        undefined,
        'external',
        trustRelationshipId,
      ),
      pathname,
    )
  ) {
    return {
      ...editTrustHeaderDefinition,
      hideHorizontalRule: true, // due to tabs
    }
  }

  if (
    trustRelationshipId &&
    matchPath(
      manageDeploymentTrustRelationshipUrl(deploymentId, undefined, 'direct', trustRelationshipId),
      pathname,
    )
  ) {
    return {
      ...editTrustHeaderDefinition,
      hideHorizontalRule: true, // due to tabs
    }
  }

  if (matchPath(operationsUrl(deploymentId), pathname)) {
    return {
      title: <FormattedMessage id='deployment-header.operations' defaultMessage='Operations' />,
      breadcrumbs: deploymentOperationsCrumbs({ deploymentId, deploymentDisplayName }),
    }
  }

  if (matchPath(heapDumpsUrl(deploymentId), pathname)) {
    return {
      title: <FormattedMessage id='deployment-header.heap-dumps' defaultMessage='Heap dumps' />,
      breadcrumbs: deploymentHeapDumpsCrumbs({ deploymentId, deploymentDisplayName }),
    }
  }

  if (matchPath(deploymentIlmMigrationUrl(deploymentId), pathname)) {
    return {
      title: (
        <FormattedMessage id='deployment-header.ilm-migration' defaultMessage='Migrate to ILM' />
      ),
      breadcrumbs: deploymentIlmMigrationCrumbs({ deploymentId, deploymentDisplayName }),
    }
  }

  return {
    title: deploymentDisplayName,
    breadcrumbs: deploymentOverviewCrumbs({ deploymentId, deploymentDisplayName }),
  }
}
